(function (global) {
  'use strict';

  // Warp coordinates and color tables are JSON data. Image CDNs may change
  // PNG pixel values when negotiating WebP/AVIF, which corrupts packed data.
  function canvas(width, height) {
    var result = document.createElement('canvas');
    result.width = width;
    result.height = height;
    return result;
  }

  function loadImage(url) {
    return fetch(url, { headers: { Accept: 'image/png' } }).then(function (response) {
      if (!response.ok || !/^image\/png(?:;|$)/i.test(response.headers.get('Content-Type') || '')) {
        throw new Error('Unable to load the original PSD texture.');
      }
      return response.blob();
    }).then(function (blob) { return global.createImageBitmap(blob, { colorSpaceConversion: 'none' }); });
  }

  function fetchData(url) {
    return fetch(url, { headers: { Accept: 'application/json' } }).then(function (response) {
      if (!response.ok) throw new Error('Unable to load PSD warp data.');
      return response.json();
    });
  }

  function decodeBytes(payload, format, channels) {
    if (!payload || payload.format !== format || !Number.isInteger(payload.width) ||
        !Number.isInteger(payload.height) || payload.width < 1 || payload.height < 1 ||
        payload.width * payload.height > 4000000 || typeof payload.data !== 'string') {
      throw new Error('Invalid PSD warp data.');
    }
    var expected = payload.width * payload.height * channels;
    if (payload.data.length !== Math.ceil(expected / 3) * 4) throw new Error('Incomplete PSD warp data.');
    var raw;
    try { raw = global.atob(payload.data); } catch (error) { throw new Error('Invalid PSD warp data encoding.'); }
    if (raw.length !== expected) throw new Error('Incomplete PSD warp data.');
    return Uint8Array.from(raw, function (character) { return character.charCodeAt(0); });
  }

  function loadSource(url) {
    if (!global.createImageBitmap) return Promise.reject(new Error('This browser cannot render the PSD color profile.'));
    return fetch(url).then(function (response) {
      if (!response.ok) throw new Error('Unable to load jersey artwork.');
      return response.blob();
    }).then(function (blob) { return global.createImageBitmap(blob, { colorSpaceConversion: 'none' }); });
  }

  function pixels(image) {
    var result = canvas(image.width, image.height);
    var context = result.getContext('2d', { willReadFrequently: true });
    context.drawImage(image, 0, 0);
    return context.getImageData(0, 0, result.width, result.height);
  }

  function decodeMap(payload) {
    var raw = decodeBytes(payload, 'uv16le-v1', 4);
    var coordinates = new Float32Array(payload.width * payload.height * 2);
    for (var index = 0; index < raw.length; index += 4) {
      var u = raw[index] + raw[index + 1] * 256;
      var v = raw[index + 2] + raw[index + 3] * 256;
      coordinates[index / 2] = u === 65535 ? -1 : u / 65534;
      coordinates[index / 2 + 1] = v === 65535 ? -1 : v / 65534;
    }
    return { width: payload.width, height: payload.height, coordinates: coordinates };
  }

  function decodeColorLut(payload, size) {
    if (!Number.isInteger(size) || size < 2 || !payload || payload.width !== size * size || payload.height !== size) {
      throw new Error('Invalid PSD color table.');
    }
    var raw = decodeBytes(payload, 'rgb8-v1', 3);
    var rgba = new Uint8ClampedArray(payload.width * payload.height * 4);
    for (var index = 0, output = 0; index < raw.length; index += 3, output += 4) {
      rgba[output] = raw[index];
      rgba[output + 1] = raw[index + 1];
      rgba[output + 2] = raw[index + 2];
      rgba[output + 3] = 255;
    }
    return rgba;
  }

  function warp(source, lookup) {
    var data = pixels(source).data;
    var result = canvas(lookup.width, lookup.height);
    var context = result.getContext('2d');
    var output = context.createImageData(result.width, result.height);
    for (var index = 0; index < lookup.coordinates.length; index += 2) {
      var u = lookup.coordinates[index], v = lookup.coordinates[index + 1];
      if (u < 0 || v < 0) continue;
      var sx = u * source.width - 0.5, sy = v * source.height - 0.5;
      var left = Math.floor(sx), top = Math.floor(sy);
      var fx = sx - left, fy = sy - top;
      var rgba = [0, 0, 0, 0];
      for (var row = 0; row < 2; row++) {
        for (var column = 0; column < 2; column++) {
          var x = left + column, y = top + row;
          if (x < 0 || y < 0 || x >= source.width || y >= source.height) continue;
          var sample = (y * source.width + x) * 4;
          var weight = (column ? fx : 1 - fx) * (row ? fy : 1 - fy);
          var alpha = data[sample + 3] / 255;
          for (var channel = 0; channel < 3; channel++) rgba[channel] += data[sample + channel] * alpha * weight;
          rgba[3] += alpha * weight;
        }
      }
      var out = index * 2;
      for (var c = 0; c < 3; c++) output.data[out + c] = rgba[3] ? rgba[c] / rgba[3] : 0;
      output.data[out + 3] = rgba[3] * 255;
    }
    context.putImageData(output, 0, 0);
    return result;
  }

  function draw(context, resource, operation) {
    context.save();
    context.globalCompositeOperation = operation || 'source-over';
    context.drawImage(resource.image, resource.x, resource.y);
    context.restore();
  }

  function clip(source, mask) {
    draw(source.getContext('2d'), mask, 'destination-in');
    return source;
  }

  function levels(value, settings) {
    var normalized = Math.max(0, Math.min(1, (value - settings.input_floor) / (settings.input_ceiling - settings.input_floor)));
    return settings.output_floor + Math.pow(normalized, 100 / settings.gamma) * (settings.output_ceiling - settings.output_floor);
  }

  function hueSaturation(rgb, settings) {
    var r = rgb[0] / 255, g = rgb[1] / 255, b = rgb[2] / 255;
    var max = Math.max(r, g, b), min = Math.min(r, g, b), delta = max - min;
    var lightness = (max + min) / 2;
    var saturation = delta ? delta / (1 - Math.abs(2 * lightness - 1)) : 0;
    var hue = !delta ? 0 : max === r ? ((g - b) / delta + 6) % 6 : max === g ? (b - r) / delta + 2 : (r - g) / delta + 4;
    hue = (hue + settings[0] / 60 + 6) % 6;
    saturation = Math.max(0, Math.min(1, saturation * (1 + settings[1] / 100)));
    var chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
    var intermediate = chroma * (1 - Math.abs(hue % 2 - 1));
    var sectors = [[chroma, intermediate, 0], [intermediate, chroma, 0], [0, chroma, intermediate], [0, intermediate, chroma], [intermediate, 0, chroma], [chroma, 0, intermediate]];
    return sectors[Math.floor(hue)].map(function (value) { return (value + lightness - chroma / 2) * 255; });
  }

  function adjust(source, first, hue, second, mask) {
    var context = source.getContext('2d', { willReadFrequently: true });
    var image = context.getImageData(0, 0, source.width, source.height);
    for (var index = 0; index < image.data.length; index += 4) {
      if (!image.data[index + 3]) continue;
      var rgb = [0, 1, 2].map(function (channel) { return levels(image.data[index + channel], first); });
      if (hue) rgb = hueSaturation(rgb, hue);
      for (var c = 0; c < 3; c++) {
        var value = rgb[c];
        if (second) value += (levels(value, second) - value) * mask[index] / 255;
        image.data[index + c] = value;
      }
    }
    context.putImageData(image, 0, 0);
    return source;
  }

  function toSrgb(source, profile) {
    var context = source.getContext('2d', { willReadFrequently: true });
    var image = context.getImageData(0, 0, source.width, source.height);
    var lut = profile.colorLut.pixels, size = profile.colorLut.size;
    for (var i = 0; i < image.data.length; i += 4) {
      if (!image.data[i + 3]) continue;
      var r = image.data[i] * (size - 1) / 255;
      var g = image.data[i + 1] * (size - 1) / 255;
      var b = image.data[i + 2] * (size - 1) / 255;
      var ri = Math.min(size - 2, Math.floor(r)), gi = Math.min(size - 2, Math.floor(g)), bi = Math.min(size - 2, Math.floor(b));
      var fr = r - ri, fg = g - gi, fb = b - bi;
      var rgb = [0, 0, 0];
      for (var dz = 0; dz < 2; dz++) for (var dy = 0; dy < 2; dy++) for (var dx = 0; dx < 2; dx++) {
        var weight = (dx ? fr : 1 - fr) * (dy ? fg : 1 - fg) * (dz ? fb : 1 - fb);
        var index = ((gi + dy) * size * size + (bi + dz) * size + ri + dx) * 4;
        for (var c = 0; c < 3; c++) rgb[c] += lut[index + c] * weight;
      }
      for (var channel = 0; channel < 3; channel++) image.data[i + channel] = rgb[channel];
    }
    context.putImageData(image, 0, 0);
    return source;
  }

  function load(url) {
    return fetchData(url).then(function (profile) {
      if (profile.version !== 2) throw new Error('Reload to update the PSD mock profile.');
      var names = Object.keys(profile).filter(function (key) { return profile[key] && profile[key].file; });
      return Promise.all(names.map(function (key) {
        var resourceUrl = new URL(profile[key].file, url).href;
        if (key === 'colorLut' || ['frontStageOne', 'frontStageTwo', 'back'].indexOf(key) !== -1) {
          return fetchData(resourceUrl).then(function (data) {
            if (key === 'colorLut') profile[key].pixels = decodeColorLut(data, profile[key].size);
            else profile[key].lookup = decodeMap(data);
          });
        }
        return loadImage(resourceUrl).then(function (image) {
          profile[key].image = image;
          if (/Mask$/.test(key)) {
            var raw = pixels(image);
            if (key === 'frontLevelsTwoMask') profile[key].pixels = raw.data;
            var converted = canvas(image.width, image.height), context = converted.getContext('2d');
            for (var i = 0; i < raw.data.length; i += 4) raw.data[i + 3] = raw.data[i];
            context.putImageData(raw, 0, 0);
            profile[key].image = converted;
          }
        });
      })).then(function () {
        var blank = canvas(profile.frontBlank.image.width, profile.frontBlank.image.height);
        blank.getContext('2d').drawImage(profile.frontBlank.image, 0, 0);
        var mask = canvas(blank.width, blank.height);
        mask.getContext('2d').drawImage(profile.frontLevelsTwoMask.image, -profile.frontBlank.x, -profile.frontBlank.y);
        profile.frontBlank.image = adjust(blank, profile.frontLevels, profile.frontHueSaturation, profile.frontLevelsTwo, pixels(mask).data);
        return profile;
      });
    });
  }

  function front(source, profile) {
    var inside = profile.frontInside;
    var first = canvas(inside.width, inside.height);
    var context = first.getContext('2d');
    context.imageSmoothingQuality = 'high';
    context.drawImage(source, inside.x, inside.y, inside.artWidth, inside.artHeight);
    clip(first, profile.frontInsideMask);
    var warped = warp(warp(first, profile.frontStageOne.lookup), profile.frontStageTwo.lookup);
    var result = canvas(profile.output[0], profile.output[1]);
    result.getContext('2d').drawImage(warped, profile.frontStageTwo.x, profile.frontStageTwo.y);
    clip(result, profile.frontMask);
    draw(result.getContext('2d'), profile.frontBlank, 'multiply');
    draw(result.getContext('2d'), profile.frontBlack);
    return result;
  }

  function render(frontLayer, backSource, profile) {
    // Photoshop first downsamples the back smart object to its transform size.
    var reduced = canvas(290, 830);
    var ctx = reduced.getContext('2d');
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(backSource, 0, 0, reduced.width, reduced.height);
    var back = canvas(profile.output[0], profile.output[1]);
    back.getContext('2d').drawImage(warp(reduced, profile.back.lookup), profile.back.x, profile.back.y);
    clip(back, profile.backMask);
    adjust(back, profile.backLevels);
    draw(back.getContext('2d'), profile.backBlank, 'multiply');
    var result = canvas(profile.output[0], profile.output[1]);
    var context = result.getContext('2d');
    context.fillStyle = '#fff';
    context.fillRect(0, 0, result.width, result.height);
    context.drawImage(frontLayer, 0, 0);
    context.drawImage(back, 0, 0);
    return toSrgb(result, profile);
  }

  global.RockEmNflJerseyPsdMock = { load: load, loadSource: loadSource, front: front, render: render, toSrgb: toSrgb };
})(window);
